// Figgo – Maskottchen der Pille (Handoff Pille §9). Darstellung (SVG-Figur, Posen per CSS-Keyframes
// „figgo…“ in index.html) und Ablauf der Szenen aus lib/figgo/scenes.ts: Bewegung, Verweilen,
// Sprechblase, Häufigkeitsregeln, Auslöser aus Pille, Zeiterfassung und Seiten-Signalen.
// Rein dekorativ (aria-hidden); aus bei prefers-reduced-motion oder per Einstellung.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import {
  bewegungMs, darfSpielen, erreichteMeilensteine, faelligeMeilensteine, figgoSzene, verweilMs, zeitLage,
  type FiggoEreignis, type FiggoGeometrie, type FiggoKontext, type FiggoPose, type FiggoSchritt,
} from '../../lib/figgo/scenes';
import { useZeit } from '../../contexts/ZeitContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useIslandState } from './IslandProvider';
import { aufFiggoSignal, useFiggoAn, type FiggoSignalOptionen } from './figgoSignal';
import { usePrefersReducedMotion } from './hooks';
import { FARBE } from './pille-stil';

/* ------------------------------------------------------------------ Figur */

type Requisit = 'cup' | 'phone' | 'lens' | 'paper' | 'broom';
/** [Animation, Grundstellung, Requisiten] eines Arms/Beins */
type Glied = [string | null, string | null, Requisit[]?];

interface PoseDef {
  body?: string; bodyO?: string; wrap?: string; head?: string; headB?: string; hip?: number;
  la?: Glied; ra?: Glied; ll?: Glied; rl?: Glied;
  smile?: boolean; closed?: boolean; big?: boolean;
  z?: boolean; conf?: boolean; balls?: boolean; bang?: boolean; ring?: boolean; stars?: boolean; dust?: boolean; plane?: boolean; laptop?: boolean; box?: boolean;
}

const G = (a: string | null, b: string | null = null, r?: Requisit[]): Glied => [a, b, r];
const STAND: PoseDef = { la: G(null, 'rotate(14deg)'), ra: G(null, 'rotate(-14deg)'), ll: G(null, 'rotate(9deg)'), rl: G(null, 'rotate(-9deg)') };
const STEP: PoseDef = { body: 'figgoBob .5s ease-in-out infinite', la: G('figgoLegA .5s ease-in-out infinite'), ra: G('figgoLegB .5s ease-in-out infinite'), ll: G('figgoLegB .5s ease-in-out infinite'), rl: G('figgoLegA .5s ease-in-out infinite') };

const POSEN: Record<FiggoPose, PoseDef> = {
  stand: STAND,
  step: STEP,
  run: { body: 'figgoRunBob .28s ease-in-out infinite', la: G('figgoRunA .28s linear infinite'), ra: G('figgoRunB .28s linear infinite'), ll: G('figgoRunB .28s linear infinite'), rl: G('figgoRunA .28s linear infinite') },
  wave: { ...STAND, ra: G('figgoWave .55s ease-in-out infinite'), smile: true },
  look: { ...STAND, head: 'figgoLook 3s ease-in-out infinite' },
  stretch: { la: G('figgoStretchL 2.6s ease-in-out infinite'), ra: G('figgoStretchR 2.6s ease-in-out infinite'), ll: G(null, 'rotate(12deg)'), rl: G(null, 'rotate(-12deg)') },
  coffee: { ...STAND, ra: G('figgoSip 3.2s ease-in-out infinite', null, ['cup']), head: 'figgoHeadSip 3.2s ease-in-out infinite' },
  juggle: { ...STAND, la: G('figgoJugL .6s ease-in-out infinite'), ra: G('figgoJugR .6s ease-in-out infinite'), balls: true, smile: true },
  dance: { body: 'figgoDance .7s ease-in-out infinite', la: G('figgoArmUpL .35s ease-in-out infinite'), ra: G('figgoArmUpR .35s ease-in-out infinite'), ll: G('figgoLegA .7s ease-in-out infinite'), rl: G('figgoLegB .7s ease-in-out infinite'), smile: true },
  trip: { wrap: 'figgoFall 2.4s ease-in-out forwards', la: G('figgoFlailL .3s linear infinite'), ra: G('figgoFlailR .3s linear infinite'), ll: G('figgoLegA .4s ease-in-out infinite'), rl: G('figgoLegB .4s ease-in-out infinite'), stars: true },
  sleep: { hip: 29, body: 'figgoBreath 2.6s ease-in-out infinite', headB: 'rotate(26deg)', la: G(null, 'rotate(10deg)'), ra: G(null, 'rotate(-10deg)'), ll: G(null, 'rotate(-80deg)'), rl: G(null, 'rotate(-92deg)'), z: true, closed: true },
  perch: { hip: 31, head: 'figgoLook 3.4s ease-in-out infinite', la: G(null, 'rotate(28deg)'), ra: G(null, 'rotate(-28deg)'), ll: G('figgoDangle 1s ease-in-out infinite'), rl: G('figgoDangle 1s ease-in-out .5s infinite'), smile: true },
  hang: { body: 'figgoSwayHang 1.8s ease-in-out infinite', bodyO: '18px 7.5px', la: G(null, 'rotate(172deg)'), ra: G(null, 'rotate(-172deg)'), ll: G('figgoKick .45s ease-in-out infinite'), rl: G('figgoKick .45s ease-in-out .22s infinite'), big: true },
  phone: { ...STAND, ra: G(null, 'rotate(-160deg)', ['phone']), head: 'figgoHeadSip 2.4s ease-in-out infinite', ring: true },
  party: { body: 'figgoJump .9s cubic-bezier(.3,.7,.4,1) infinite', la: G('figgoArmUpL .45s ease-in-out infinite'), ra: G('figgoArmUpR .45s ease-in-out infinite'), ll: G(null, 'rotate(14deg)'), rl: G(null, 'rotate(-14deg)'), conf: true, smile: true },
  shock: { body: 'figgoShake .16s linear infinite', la: G(null, 'rotate(140deg)'), ra: G(null, 'rotate(-140deg)'), ll: G(null, 'rotate(16deg)'), rl: G(null, 'rotate(-16deg)'), bang: true, big: true },
  facepalm: { ...STAND, body: 'figgoSigh 2s ease-in-out infinite', ra: G(null, 'rotate(-158deg)'), headB: 'rotate(10deg)' },
  carry: { ...STEP, la: G(null, 'rotate(168deg)'), ra: G(null, 'rotate(-168deg)'), box: true },
  lift: { ...STAND, body: 'figgoLift 1.2s ease-in-out infinite', la: G(null, 'rotate(168deg)'), ra: G(null, 'rotate(-168deg)'), box: true },
  magnify: { ...STAND, ra: G('figgoMag 1.4s ease-in-out infinite', null, ['lens']), head: 'figgoLook 2.8s ease-in-out infinite' },
  read: { ...STAND, la: G(null, 'rotate(-62deg)'), ra: G(null, 'rotate(-98deg)', ['paper']), head: 'figgoNod 1.6s ease-in-out infinite' },
  shrug: { ...STAND, body: 'figgoShrug .8s ease-in-out infinite', la: G(null, 'rotate(128deg)'), ra: G(null, 'rotate(-128deg)'), headB: 'rotate(-10deg)' },
  sweep: { body: 'figgoBob .6s ease-in-out infinite', la: G(null, 'rotate(-40deg)'), ra: G('figgoSweep .6s ease-in-out infinite', null, ['broom']), ll: G('figgoLegB .6s ease-in-out infinite'), rl: G('figgoLegA .6s ease-in-out infinite'), dust: true, smile: true },
  point: { ...STAND, ra: G('figgoPoint 1s ease-in-out infinite'), smile: true },
  throw: { ...STAND, ra: G('figgoThrow 1s cubic-bezier(.3,.6,.3,1) forwards'), plane: true, smile: true },
};

const INK = '#fff';
const GOLD = FARBE.gold;

function Linie({ x1, y1, x2, y2 }: { x1: number; y1: number; x2: number; y2: number }) {
  return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={INK} strokeWidth={2} strokeLinecap="round" />;
}

function Gruppe({ anim, origin, base, children }: { anim?: string | null; origin: string; base?: string | null; children: ReactNode }) {
  return <g style={{ transformBox: 'view-box', transformOrigin: origin, transform: base ?? undefined, animation: anim ?? 'none' }}>{children}</g>;
}

function RequisitSvg({ n, sh }: { n: Requisit; sh: number }) {
  switch (n) {
    case 'cup': return (
      <g>
        <rect x={15.6} y={sh + 6.4} width={4.6} height={3.8} rx={1} fill={GOLD} />
        <path d={`M17 ${sh + 5.2} q1 -1.4 0 -2.6`} stroke="rgba(255,255,255,0.75)" strokeWidth={0.9} fill="none" strokeLinecap="round" style={{ animation: 'figgoSteam 1.6s ease-out infinite' }} />
      </g>
    );
    case 'phone': return <rect x={16.4} y={sh + 5.4} width={3.2} height={5.4} rx={1} fill={GOLD} />;
    case 'lens': return <circle cx={18} cy={sh + 10.4} r={2.8} fill="rgba(226,192,141,0.2)" stroke={GOLD} strokeWidth={1.4} />;
    case 'paper': return (
      <g>
        <rect x={15} y={sh + 6} width={7.5} height={5.6} rx={0.8} fill="rgba(255,255,255,0.93)" />
        <line x1={16.2} y1={sh + 7.8} x2={21.2} y2={sh + 7.8} stroke="#888" strokeWidth={0.6} />
        <line x1={16.2} y1={sh + 9.4} x2={20} y2={sh + 9.4} stroke="#888" strokeWidth={0.6} />
      </g>
    );
    case 'broom': return (
      <g>
        <line x1={18} y1={sh + 7.5} x2={18} y2={sh + 17.5} stroke={GOLD} strokeWidth={1.6} strokeLinecap="round" />
        <path d={`M15.4 ${sh + 17.5} L20.6 ${sh + 17.5} L19.6 ${sh + 21} L16.4 ${sh + 21} Z`} fill={GOLD} />
      </g>
    );
  }
}

/** Die Figur (SVG 36×32, Maßstab s) – Zeichnung wie Prototyp `Fig`. */
export function FiggoFigur({ pose, flip = false, s = 1 }: { pose: FiggoPose; flip?: boolean; s?: number }) {
  const P = POSEN[pose] ?? STAND;
  const hip = P.hip ?? 22;
  const sh = hip - 7;
  const hy = hip - 13;
  const arm = (g: Glied) => (
    <Gruppe anim={g[0]} origin={`18px ${sh}px`} base={g[1]}>
      <Linie x1={18} y1={sh} x2={18} y2={sh + 7.5} />
      {(g[2] ?? []).map(n => <RequisitSvg key={n} n={n} sh={sh} />)}
    </Gruppe>
  );
  const bein = (g: Glied) => (
    <Gruppe anim={g[0]} origin={`18px ${hip}px`} base={g[1]}><Linie x1={18} y1={hip} x2={18} y2={hip + 9} /></Gruppe>
  );
  const auge = P.closed
    ? <line x1={18.8} y1={hy} x2={20.8} y2={hy} stroke={INK} strokeWidth={1} strokeLinecap="round" />
    : P.big
      ? <circle cx={19.7} cy={hy - 0.4} r={1.15} fill="none" stroke={INK} strokeWidth={0.9} />
      : <circle cx={19.6} cy={hy - 0.4} r={0.75} fill={INK} />;
  const kopf = (
    <Gruppe anim={P.head} origin={`18px ${hy + 3}px`} base={P.headB}>
      <circle cx={18} cy={hy} r={3.7} fill="#0b0b0e" stroke={INK} strokeWidth={2} />
      {auge}
      {P.smile && <path d={`M18.8 ${hy + 1.3} q1.2 1.1 2.4 0`} stroke={INK} strokeWidth={0.9} fill="none" strokeLinecap="round" />}
      <path d={`M14.6 ${hy - 1.6} q3.4 -4.2 7 0`} stroke={GOLD} strokeWidth={1.6} fill="none" strokeLinecap="round" />
    </Gruppe>
  );
  const koerper = (
    <Gruppe anim={P.body} origin={P.bodyO ?? '18px 31px'}>
      {arm(P.la ?? STAND.la!)}
      {bein(P.ll ?? STAND.ll!)}
      <Linie x1={18} y1={hy + 3.7} x2={18} y2={hip} />
      {bein(P.rl ?? STAND.rl!)}
      {kopf}
      {arm(P.ra ?? STAND.ra!)}
      {P.box && <rect x={13.5} y={hy - 9.6} width={9} height={5.6} rx={1} fill={GOLD} />}
    </Gruppe>
  );
  return (
    <svg width={36 * s} height={32 * s} viewBox="0 0 36 32" style={{ overflow: 'visible', display: 'block' }} aria-hidden>
      <g style={{ transformBox: 'view-box', transformOrigin: '18px 16px', transform: flip ? 'scaleX(-1)' : 'none' }}>
        <g style={{ transformBox: 'view-box', transformOrigin: '18px 31px', animation: P.wrap ?? 'none' }}>{koerper}</g>
        {P.laptop && (
          <g>
            <rect x={23.5} y={24} width={8} height={1.8} rx={0.6} fill="rgba(255,255,255,0.85)" />
            <rect x={29.3} y={17.6} width={1.8} height={7} rx={0.6} fill={GOLD} transform="rotate(-12 30 24)" />
          </g>
        )}
        {P.z && [0, 1].map(i => (
          <text key={i} x={23 + i * 3} y={hy - 2 - i * 2} fontSize={5 + i} fontWeight={800} fill="rgba(255,255,255,0.85)" style={{ animation: `figgoZ 2.4s ease-out ${i * 1.2}s infinite` }}>z</text>
        ))}
        {P.conf && ([[8, '#ff6482'], [28, '#64d2ff'], [12, '#30d158'], [25, GOLD]] as const).map(([x, c], i) => (
          <circle key={i} cx={x} cy={6} r={1.1} fill={c} style={{ animation: `figgoConf 1s ease-out ${i * 0.22}s infinite` }} />
        ))}
        {P.balls && ([[13, '#ff6482'], [18, '#64d2ff'], [23, GOLD]] as const).map(([x, c], i) => (
          <circle key={i} cx={x} cy={2.5} r={1.4} fill={c} style={{ animation: `figgoBall .6s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
        {P.bang && <text x={24.5} y={6} fontSize={8} fontWeight={900} fill="#ff6961">!</text>}
        {P.ring && <circle cx={23.5} cy={hy} r={3} fill="none" stroke="rgba(48,209,88,0.9)" strokeWidth={1} style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'figgoRing 1.4s ease-out infinite' }} />}
        {P.stars && (
          <g style={{ animation: 'figgoStar 2.4s linear forwards' }}>
            <text x={29} y={22} fontSize={5} fontWeight={900} fill={GOLD}>*</text>
            <text x={33} y={25} fontSize={4} fontWeight={900} fill={GOLD}>*</text>
            <text x={30.5} y={27.5} fontSize={4} fontWeight={900} fill={GOLD}>*</text>
          </g>
        )}
        {P.dust && [0, 1, 2].map(i => (
          <circle key={i} cx={14 + i * 1.5} cy={30} r={1.1} fill="rgba(255,255,255,0.45)" style={{ animation: `figgoDust .9s ease-out ${i * 0.3}s infinite` }} />
        ))}
        {P.plane && <polygon points="22,9 28.5,11 22,12.4 23.5,10.8" fill="#fff" style={{ animation: 'figgoPlane 1.2s ease-out forwards' }} />}
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------------ Ablauf */

interface Akteur {
  x: number; y: number; pose: FiggoPose; flip: boolean;
  /** Dauer der Bewegung zum aktuellen Punkt */
  tr: number;
  say: string;
  s: number; offen: boolean; sayLeft: boolean; sayBelow: boolean;
}

export interface FiggoZustand { da: boolean; spricht: boolean }

interface FiggoProps {
  pillRef: RefObject<HTMLDivElement | null>;
  /** Zielgröße der Pille (so, wie sie gerade animiert wird) */
  ziel: { w: number; h: number };
  hovered: boolean;
  onZustand: (z: FiggoZustand) => void;
}

type SpielOptionen = FiggoSignalOptionen & { m?: number; versuche?: number };

export function Figgo({ pillRef, ziel, hovered, onZustand }: FiggoProps) {
  const an = useFiggoAn();
  const reduziert = usePrefersReducedMotion();
  const aktiv = an && !reduziert;
  const state = useIslandState();
  const { mode, deckPage, toast, pageLabel } = state;
  const { unreadCount } = useNotification();
  const zeit = useZeit();

  const [akt, setAkt] = useState<Akteur | null>(null);
  const aktRef = useRef<Akteur | null>(null);
  aktRef.current = akt;
  const timer = useRef<number[]>([]);
  const lauf = useRef<object | null>(null);
  const letzterStart = useRef(0);
  const nachfrage = useRef<number | null>(null);
  const zielRef = useRef(ziel);
  zielRef.current = ziel;
  const offen = mode === 'deck' || mode === 'form';
  const offenRef = useRef(offen);
  offenRef.current = offen;

  // Kontext für die Szenen (immer frisch über Ref, Timer lesen ihn später)
  const offenePause = zeit.laufend?.pausen?.find(p => !p.ende) ?? null;
  const kontext: FiggoKontext = {
    stunde: new Date().getHours(),
    wochentag: new Date().getDay(),
    zeit: zeitLage({
      laufend: !!zeit.laufend, inPause: zeit.inPause, heuteMinuten: zeit.heuteMinuten,
      pauseSeit: offenePause?.beginn ?? null,
      pausenHeute: zeit.heuteStempel.reduce((n, st) => n + (st.pausen?.length ?? 0), 0),
      heuteGestempelt: zeit.heuteStempel.length > 0,
      jetzt: zeit.jetzt,
    }),
    bereich: pageLabel,
    ungelesen: unreadCount,
    // Umgestellte Pillen-Formulare melden über meta.pflichtHinweis, ob sie Pflichtfelder haben.
    formPflicht: state.formMeta ? !!state.formMeta.pflichtHinweis : undefined,
  };
  const kontextRef = useRef(kontext);
  kontextRef.current = kontext;

  useEffect(() => { onZustand({ da: !!akt, spricht: !!akt?.say }); }, [akt, onZustand]);

  const stopp = useCallback(() => {
    timer.current.forEach(t => window.clearTimeout(t));
    timer.current = [];
    lauf.current = null;
    setAkt(null);
  }, []);

  const geometrie = useCallback((): (FiggoGeometrie & { bewegt: boolean; H: number }) | null => {
    const el = pillRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const o = offenRef.current;
    const s = o ? 1.25 : 1;
    const { w: W, h: H } = zielRef.current;
    const cur = aktRef.current;
    return {
      s, W, H,
      bewegt: Math.abs(r.width - W) > 3 || Math.abs(r.height - H) > 3,
      floor: o ? H - 31 * s - 6 : H - 31 - 2,
      aktuell: cur ? { x: cur.x, y: cur.y } : null,
      anker: (name) => {
        const a = el.querySelector(`[data-bj="${name}"]`);
        if (!a) return null;
        const q = a.getBoundingClientRect();
        const y = q.top - r.top;
        if (y < 30 || y > H - 30) return null;
        return { x: q.left - r.left, y, w: q.width, top: y - 31 * s };
      },
    };
  }, [pillRef]);

  const ablaufen = useCallback((steps: FiggoSchritt[], spawn?: { x: number; y: number; pose: FiggoPose }) => {
    timer.current.forEach(t => window.clearTimeout(t));
    timer.current = [];
    letzterStart.current = Date.now();
    const run = {};
    lauf.current = run;
    const o = offenRef.current;
    const s = o ? 1.25 : 1;
    const W = zielRef.current.w;
    const go = (i: number) => {
      if (lauf.current !== run) return;
      if (i >= steps.length) { lauf.current = null; setAkt(null); return; }
      const st = steps[i]!;
      const cur = aktRef.current;
      const dist = cur ? Math.hypot(st.x - cur.x, st.y - cur.y) : 0;
      const ms = bewegungMs(dist, st, o);
      const flip = cur && Math.abs(st.x - cur.x) > 1 ? st.x < cur.x : cur ? cur.flip : false;
      const neu: Akteur = { x: st.x, y: st.y, pose: st.pose, flip, tr: ms, say: st.say ?? '', s, offen: o, sayLeft: st.x > W / 2, sayBelow: o && st.y - 4 * s < 66 };
      aktRef.current = neu;
      setAkt(neu);
      const nxt = steps[i + 1];
      const verlaesst = !!nxt && (nxt.x < -20 || nxt.x > W);
      timer.current.push(window.setTimeout(() => go(i + 1), ms + verweilMs(st.hold, verlaesst, i)));
    };
    if (!aktRef.current && spawn) {
      const start: Akteur = { ...spawn, flip: spawn.x > W / 2, tr: 0, say: '', s, offen: o, sayLeft: false, sayBelow: false };
      aktRef.current = start;
      setAkt(start);
      timer.current.push(window.setTimeout(() => go(0), 60));
    } else {
      go(0);
    }
  }, []);

  const spielen = useCallback((kind: FiggoEreignis, opt: SpielOptionen = {}) => {
    if (!darfSpielen({
      reduziert, aus: !an, force: !!opt.force, laeuft: !!lauf.current, jetzt: Date.now(), letzterStart: letzterStart.current,
      cool: opt.cool, wahrscheinlichkeit: opt.p, zufall: Math.random,
    })) return;
    const g = geometrie();
    if (!g) return;
    if (g.bewegt && (opt.versuche ?? 0) < 6) {
      if (nachfrage.current !== null) window.clearTimeout(nachfrage.current);
      // erneut ohne Zufall/Abkühlung, die Entscheidung ist schon gefallen
      nachfrage.current = window.setTimeout(() => spielen(kind, { ...opt, force: true, p: 1, versuche: (opt.versuche ?? 0) + 1 }), 180);
      return;
    }
    const sz = figgoSzene(kind, g, { ...kontextRef.current, m: opt.m }, Math.random);
    if (!sz) return;
    ablaufen(sz.steps, sz.spawn);
  }, [an, reduziert, geometrie, ablaufen]);

  // Aus (Einstellung/reduzierte Bewegung) → sofort weg
  useEffect(() => { if (!aktiv) stopp(); }, [aktiv, stopp]);
  useEffect(() => () => {
    timer.current.forEach(t => window.clearTimeout(t));
    if (nachfrage.current !== null) window.clearTimeout(nachfrage.current);
  }, []);

  // Besuche: erster nach 3,5 s, dann alle 26 s mit 50 % (≥ 20 s Abstand), nur ruhend und ohne Hover
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const hoverRef = useRef(hovered);
  hoverRef.current = hovered;
  useEffect(() => {
    if (!aktiv) return;
    const erst = window.setTimeout(() => { if (modeRef.current === 'idle') spielen('visit', { force: true }); }, 3500);
    const takt = window.setInterval(() => { if (modeRef.current === 'idle' && !hoverRef.current) spielen('visit', { p: 0.5, cool: 20000 }); }, 26000);
    return () => { window.clearTimeout(erst); window.clearInterval(takt); };
  }, [aktiv, spielen]);

  // Meilensteine 6/8/9/10 h je Stempelsitzung einmal (Prüfung alle 15 s)
  const gespielt = useRef(new Map<string, Set<number>>());
  useEffect(() => {
    if (!aktiv) return;
    const pruefen = () => {
      const z = kontextRef.current.zeit;
      const sitzung = zeit.laufend?.id;
      if (!sitzung || z.st !== 'arbeit') return;
      const schon = gespielt.current.get(sitzung) ?? new Set<number>();
      const faellig = faelligeMeilensteine(z.min, schon);
      erreichteMeilensteine(z.min).forEach(t => schon.add(t));
      gespielt.current.set(sitzung, schon);
      const m = faellig[faellig.length - 1];
      if (m !== undefined) spielen('meilenstein', { force: !lauf.current, m });
    };
    const t = window.setInterval(pruefen, 15000);
    return () => window.clearInterval(t);
  }, [aktiv, spielen, zeit.laufend?.id]);

  // Pillen-Zustände: Öffnen/Schließen, Meldung, Aktivität, Anruf, Seitenwechsel
  const deckPageRef = useRef(deckPage);
  deckPageRef.current = deckPage;
  const seiteVon = (m: typeof mode, d: typeof deckPage): string | null =>
    m === 'form' ? 'form' : m === 'deck' ? `page:${d}` : null;
  const vorher = useRef({ mode, seite: seiteVon(mode, deckPage) });
  const seitenTimer = useRef<number | null>(null);
  useEffect(() => {
    const ps = vorher.current;
    const seite = seiteVon(mode, deckPage);
    vorher.current = { mode, seite };
    if (!aktiv) return;
    const warOffen = ps.mode === 'deck' || ps.mode === 'form';
    if (aktRef.current && offen !== warOffen) stopp();
    if (mode !== ps.mode) {
      if (mode === 'toast' && toast) {
        if (toast.tone === 'success') spielen('success', { force: true, p: 0.9 });
        else if (toast.tone === 'error') spielen('error', { force: true, p: 0.9 });
      } else if (mode === 'live') spielen('live', { force: true, p: 0.9 });
      else if (mode === 'anruf') spielen('anruf', { force: true, p: 0.75 });
      const c = aktRef.current;
      if (ps.mode === 'live' && mode !== 'toast' && c) {
        ablaufen([{ x: c.x < zielRef.current.w / 2 ? -44 : zielRef.current.w + 8, y: c.y, pose: 'step' }]);
      }
    }
    if (seite && seite !== ps.seite) {
      if (aktRef.current && ps.seite) stopp();
      if (seitenTimer.current !== null) window.clearTimeout(seitenTimer.current);
      seitenTimer.current = window.setTimeout(() => {
        if (seiteVon(modeRef.current, deckPageRef.current) === seite) spielen(seite as FiggoEreignis, { p: 0.55, cool: 7000 });
      }, 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, deckPage, offen, aktiv]);
  useEffect(() => () => { if (seitenTimer.current !== null) window.clearTimeout(seitenTimer.current); }, []);

  // Stempeln: Kommen / Pause / Weiter / Gehen (nur echte Wechsel nach dem ersten Laden)
  const zeitVorher = useRef<{ bereit: boolean; laufend: boolean; pause: boolean; min: number } | null>(null);
  useEffect(() => {
    const jetzt = { bereit: zeit.bereit, laufend: !!zeit.laufend, pause: zeit.inPause, min: zeit.heuteMinuten };
    const ps = zeitVorher.current;
    zeitVorher.current = jetzt;
    if (!aktiv || !ps || !ps.bereit || !jetzt.bereit) return;
    if (jetzt.laufend && !ps.laufend) spielen('kommen', { force: true, p: 0.9 });
    else if (jetzt.pause && !ps.pause) spielen('pause', { force: true, p: 0.9 });
    else if (!jetzt.pause && ps.pause && jetzt.laufend) spielen('weiter', { force: true, p: 0.9 });
    else if (!jetzt.laufend && ps.laufend) spielen('gehen', { force: true, p: 0.9, m: ps.min });
  }, [zeit.bereit, zeit.laufend, zeit.inPause, zeit.heuteMinuten, aktiv, spielen]);

  // Signale aus den Seiten (Suche, Telefon, Meldungen, Chat)
  useEffect(() => aufFiggoSignal((kind, o) => spielen(kind, o)), [spielen]);

  if (!aktiv || !akt) return null;
  return <FiggoAkteur a={akt} sprechMax={Math.max(120, ziel.w - 56 - 16)} onPoke={() => spielen('poke', { force: true })} />;
}

function FiggoAkteur({ a, sprechMax, onPoke }: { a: Akteur; sprechMax: number; onPoke: () => void }) {
  const W = 36 * a.s;
  const blase: CSSProperties = {
    position: 'absolute',
    top: a.offen ? (a.sayBelow ? Math.max(-4 * a.s, 68 - a.y) : -4 * a.s) : 16 * a.s - 13,
    [a.sayLeft ? 'right' : 'left']: a.offen ? W + 2 : W - 4,
    whiteSpace: 'nowrap', padding: a.offen ? '6px 11px' : '5px 10px', lineHeight: a.offen ? 'normal' : '16px',
    width: 'max-content', maxWidth: a.offen ? 'none' : sprechMax, overflow: 'hidden', textOverflow: 'ellipsis',
    borderRadius: a.sayBelow ? (a.sayLeft ? '14px 4px 14px 14px' : '4px 14px 14px 14px') : a.sayLeft ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
    zIndex: 2, background: 'rgba(255,255,255,0.95)', color: '#111', fontSize: a.offen ? 12 : 11.5, fontWeight: 700,
    boxShadow: '0 8px 20px -8px rgba(0,0,0,0.7)', animation: 'figgoTalk .3s cubic-bezier(.34,1.56,.64,1) both', pointerEvents: 'none',
  };
  return (
    <span
      data-figgo=""
      aria-hidden
      title="Figgo"
      onClick={(e) => { e.stopPropagation(); onPoke(); }}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', left: a.x, top: a.y, width: W, height: 32 * a.s, zIndex: 6, pointerEvents: 'auto', cursor: 'pointer',
        transition: `left ${a.tr}ms linear, top ${Math.min(a.tr, 320)}ms ease`,
      }}
    >
      <FiggoFigur key={a.pose} pose={a.pose} flip={a.flip} s={a.s} />
      {a.say && <span key={a.say} style={blase}><b style={{ color: '#9c7440' }}>Figgo </b>{a.say}</span>}
    </span>
  );
}
